import { createServerFn } from "@tanstack/react-start";

import { CCOType } from "~/lib/schemas/db-enums";

// Defined in `shared/` so client/test code can reach it without this module's
// server graph; re-exported here so `~/lib/ccos` stays the one public surface
// for CCO domain constants.
export { CCO_TYPE_ORDER } from "./shared/cco-type-order";

export type CCO = {
  id: string;
  name: string;
  logoUrl: string | null;
  type: CCOType;
  slug: string | null;
  website: string | null;
  description: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;
  subcategory: string | null;
  coaches: number; // Aggregate count
  frameworks: number; // Published frameworks originated by this CCO
  ctps: number; // Published training programs accredited by this CCO
};

/** An organization with its coach count only, for the Builder carousel. */
export type CcoWithCoachCount = Omit<CCO, "frameworks" | "ctps">;

// Define the structure for the two-level grouping
export type GroupedCCOsByType = {
  [type in CCOType]?: {
    // Optional because a type might have 0 CCOs
    [subcategory: string]: CCO[]; // Use 'Uncategorized' string key directly
  };
};

/** The public directory: its organizations and the coach minimum for cards. */
export type CcoDirectory = {
  organizations: GroupedCCOsByType;
  /** Fewest coaches an organization needs for its card to show a coach count. */
  directoryMinCoaches: number;
};

// Define a constant for the uncategorized key for clarity
export const UNCATEGORIZED_KEY = "Uncategorized";

// Mapping from CCOType to user-facing display name (plural — used for
// group headings on the Organizations index page).
export const CCO_TYPE_DISPLAY_NAME: Record<CCOType, string> = {
  [CCOType.PAO]: "Professional Accreditation Organizations",
  [CCOType.CTO]: "Coach Training Organizations",
};

// Singular form — use on detail-page kickers, "Type" rows, and anywhere a
// single organization is referenced (vs. a grouped list).
export const CCO_TYPE_DISPLAY_NAME_SINGULAR: Record<CCOType, string> = {
  [CCOType.PAO]: "Professional Accreditation Organization",
  [CCOType.CTO]: "Coach Training Organization",
};

// Public: published, canonical organizations only. The coach qualifications
// picker reads drafts through `getQualificationPickerOptions`.
export const getGroupedCCOs = createServerFn({ method: "GET" }).handler(
  async (): Promise<CcoDirectory> => {
    const { getCcoDirectory } = await import("~/lib/server/cco-directory");
    return getCcoDirectory();
  },
);

export const getCcos = createServerFn({ method: "GET" })
  .validator((input: unknown) => {
    const limit =
      typeof input === "object" && input !== null && "limit" in input
        ? Number((input as { limit: unknown }).limit) || 100
        : 100;
    return { limit };
  })
  .handler(async ({ data }): Promise<{ ccos: CcoWithCoachCount[] }> => {
    const { listPublishedCcosByCoachCount } =
      await import("~/lib/server/cco-directory");
    return { ccos: await listPublishedCcosByCoachCount({ limit: data.limit }) };
  });
